// stocai_frontend/src/components/HomePage/components/Action/MyActionsIntro.jsx

import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom"; // ✅ ADD THIS
import { UserContext } from "../../../../UserContext";
import actionPlanService from "../../../../services/actionPlanService";
import actionPot1 from "../../../../assets/action_pot1.png";
import pot2 from "../../../../assets/pot2.png";
import concentricArrow from "../../../../assets/concentric_arrow.png";
import concentricDull from "../../../../assets/concentric_dull.png";
import arrow from "../../../../assets/arrow.png";
import meterIcon from "../../../../assets/meter.png";
import pot3 from "../../../../assets/pot3.png";
import fullPot from "../../../../assets/full_pot.png";
import CheckIn from "./CheckIn";
import SaveGoals from "./SaveGoals";

// ✅ Use same API base URL pattern as progressService
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000/api';

// Remove '/api' suffix if present since action-plan endpoint doesn't use it
const ACTION_PLAN_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

// ✅ Helper function to determine which pot image to use
const getPotImage = (pot) => {
  if (!pot) return pot3;
  
  const hasActiveObjectives = pot.objectives.some(obj => obj.status === "active");
  
  // All objectives completed - show full pot
  if (!hasActiveObjectives && pot.objectives.length > 0) {
    return fullPot;
  }
  
  // XP > 0 - show budding sapling
  if (pot.xp_total > 0) {
    return pot2;
  }
  
  // XP = 0 - show initial pot
  return pot3;
};

const LeftPotPanel = ({ pot, onCheckIn, onCompleteGoal }) => {
  if (!pot) return null;

  const potImage = getPotImage(pot);

  return (
    <div className="w-[260px] flex flex-col items-center pt-4 px-8">
      <img
        src={potImage}
        alt={pot.title}
        className="w-[102px] h-[128px] object-contain mb-6"
      />

      <h3 className="text-[19px] leading-[1.35] font-medium text-black text-center">
        {pot.title}
      </h3>

      <p className="mt-1 text-[14px] leading-[1.35] font-medium uppercase text-[#54B0AF] text-center">
        {pot.xp_total}XP GAINED
      </p>

      <button
        type="button"
        onClick={onCheckIn}
        className="mt-6 w-[231px] h-[48px] rounded-full border-2 border-black bg-black flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
      >
        <span className="text-[18px] font-medium text-white">Check-in</span>
      </button>

      <button
        type="button"
        onClick={onCompleteGoal}
        className="mt-3 w-[231px] h-[48px] rounded-full border-2 border-black bg-black flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
      >
        <span className="text-[18px] font-medium text-white">
          Complete Goal
        </span>
      </button>

      <p className="mt-6 w-[340px] text-[14px] leading-[1.45] text-center text-[#808080]">
        Check in regularly water your pot, earn XP
        <br />
        and keep your momentum going.
      </p>
      <p className="mt-4 w-[340px] text-[14px] leading-[1.45] text-center text-[#808080]">
        Complete goals to grow your plant.
      </p>
    </div>
  );
};

const RightInitiativesPanel = ({ pot, selectedObjectiveId, onObjectiveClick }) => {
  const lineStartRef = useRef(null);
  const lastArrowRef = useRef(null);
  const [lineHeight, setLineHeight] = useState(400);

  const allObjectives = pot?.objectives || [];
  const selectedObjective = allObjectives.find(obj => obj.id === selectedObjectiveId);

  const shortTermInitiatives = selectedObjective?.initiatives?.filter(
    (init) => init.horizon === "short_term"
  ) || [];
  
  const mediumTermInitiatives = selectedObjective?.initiatives?.filter(
    (init) => init.horizon === "medium_term"
  ) || [];

  const hasShortTerm = shortTermInitiatives.length > 0;
  const hasMediumTerm = mediumTermInitiatives.length > 0;

  useEffect(() => {
    if (!selectedObjective || !lineStartRef.current || !lastArrowRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (lineStartRef.current && lastArrowRef.current) {
        try {
          const startRect = lineStartRef.current.getBoundingClientRect();
          const arrowRect = lastArrowRef.current.getBoundingClientRect();
          const calculatedHeight = arrowRect.top - startRect.top + 4;
          setLineHeight(calculatedHeight);
        } catch (error) {
          console.error("Error calculating line height:", error);
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [selectedObjective, shortTermInitiatives.length, mediumTermInitiatives.length]);

  if (!pot || !pot.objectives || pot.objectives.length === 0) {
    return (
      <div className="flex-1 left-16 relative pt-0">
        <div className="pl-16 pr-6">
          <p className="text-[14px] text-gray-500 mt-4">No objectives yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 left-16 relative pt-0">
      <div className="space-y-6">
        {allObjectives.map((objective) => {
          const isSelected = objective.id === selectedObjectiveId;
          const isCompleted = objective.status === "completed";

          if (!isSelected || isCompleted) {
            return (
              <div
                key={objective.id}
                className="flex items-center cursor-pointer hover:opacity-80"
                onClick={() => !isCompleted && onObjectiveClick(objective.id)}
              >
                <img
                  src={concentricDull}
                  alt=""
                  className="w-[38px] h-[38px] object-cover mr-4"
                />
                <p
                  className={`text-[21px] leading-[1.35] font-medium ${
                    isCompleted
                      ? "text-[#939393] line-through"
                      : "text-[#939393]"
                  }`}
                >
                  {objective.title}
                </p>
              </div>
            );
          }

          return (
            <div key={objective.id} className="relative">
              <div ref={lineStartRef} className="absolute left-0 top-0 w-0 h-0" />
              
              <div 
                className="absolute left-[17px] top-0 border-l border-[#D1D1D1]"
                style={{ height: `${lineHeight}px` }}
              />

              <img
                src={concentricArrow}
                alt=""
                className="absolute left-0 top-0 w-[38px] h-[38px]"
              />

              <div className="pl-16 pr-6">
                <h3 className="text-[21px] leading-[1.35] font-medium text-[#424242] w-[520px] mb-2">
                  {objective.title}
                </h3>

                {!hasShortTerm && !hasMediumTerm && (
                  <p className="text-[14px] text-gray-500 mt-4">No initiatives yet</p>
                )}

                {hasShortTerm && (
                  <div className="mt-6 mb-8">
                    <p className="text-[11px] font-bold text-[#54B0AF] uppercase mb-3">
                      SHORT TERM INITIATIVES
                    </p>

                    <div className="space-y-4">
                      {shortTermInitiatives.map((item, index) => {
                        const isLastArrow = !hasMediumTerm && index === shortTermInitiatives.length - 1;
                        
                        return (
                          <div key={item.id} className="relative">
                            <img
                              ref={isLastArrow ? lastArrowRef : null}
                              src={arrow}
                              alt=""
                              className="absolute left-[-46px] top-[10px] w-[17.6px] h-auto object-contain"
                            />
                            
                            <div className="flex items-center gap-4">
                              <img
                                src={meterIcon}
                                alt=""
                                className="w-[30px] h-[30px] object-contain"
                              />

                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <p className="text-[15px] leading-[1.45] text-black mr-4">
                                    {item.title}
                                  </p>
                                  <span className="text-[12px] mr-12 font-medium text-[#54B0AF] uppercase whitespace-nowrap">
                                    +{item.xp_value}XP
                                  </span>
                                </div>

                                <div className="mt-2">
                                  <div className="w-[695px] border-b border-[#E4E4E4]" />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {hasMediumTerm && (
                  <div className="mb-8">
                    <p className="text-[11px] font-bold text-[#54B0AF] uppercase mb-3">
                      MEDIUM TERM INITIATIVES
                    </p>

                    <div className="space-y-4">
                      {mediumTermInitiatives.map((item, index) => {
                        const isLastArrow = index === mediumTermInitiatives.length - 1;
                        
                        return (
                          <div key={item.id} className="relative">
                            <img
                              ref={isLastArrow ? lastArrowRef : null}
                              src={arrow}
                              alt=""
                              className="absolute left-[-46px] top-[10px] w-[17.6px] h-auto object-contain"
                            />
                            
                            <div className="flex items-center gap-4">
                              <img
                                src={meterIcon}
                                alt=""
                                className="w-[30px] h-[30px] object-contain"
                              />

                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <p className="text-[15px] leading-[1.45] text-black mr-4">
                                    {item.title}
                                  </p>
                                  <span className="text-[12px] mr-12 font-medium text-[#54B0AF] uppercase whitespace-nowrap">
                                    +{item.xp_value}XP
                                  </span>
                                </div>

                                <div className="mt-2">
                                  <div className="w-[695px] border-b border-[#E4E4E4]" />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GoalPotsSection = ({ allPots, selectedPotId, onPotClick }) => {
  const gridPots = allPots.filter((pot) => pot.id !== selectedPotId);

  return (
    <div className="mt-8 pt-8 px-10 pb-8 flex items-end justify-between">
      <div className="w-[280px]">
        <h3 className="text-[24px] leading-[1.35] font-medium text-black">
          Your Goal Pots
        </h3>
        <p className="mt-1 text-[14px] leading-[1.45] text-[#B2B2B2]">
          Click to see details and water your pot
        </p>
      </div>

      <div className="flex flex-1 justify-end gap-6">
        {gridPots.map((pot) => {
          const potImage = getPotImage(pot);
          
          return (
            <div
              key={pot.id}
              onClick={() => onPotClick(pot.id)} // ✅ WHOLE CARD CLICKABLE
              className="
                relative
                w-[172px] h-[184px]
                rounded-[10px]
                border border-[#DEDEDE]
                bg-white
                flex flex-col items-center pt-6 pb-10
                cursor-pointer
                hover:border-[#54B0AF]
                hover:shadow-md
                transition-all
              "
            >
              <img
                src={potImage}
                alt={pot.title}
                className="w-[75px] h-[71px] object-contain mb-4"
              />

              <p className="text-[15px] leading-[1.25] font-medium text-black text-center px-3">
                {pot.title}
              </p>

              <div
                className="
                  absolute
                  left-1/2
                  bottom-[-13px]
                  -translate-x-1/2
                  w-[102.96px]
                  h-[25.88px]
                  rounded-[21.56px]
                  bg-black
                  flex items-center justify-center
                  group-hover:bg-gray-800
                  transition-colors
                "
              >
                <span className="text-[9.7px] leading-[1] font-medium uppercase text-white">
                  See Details
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const MyActionsIntro = () => { // ✅ REMOVED onGoToActionPlan prop
  const { user } = useContext(UserContext);
  const userId = user?.user_id;
  const navigate = useNavigate(); // ✅ ADD THIS

  const [actionPlan, setActionPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPotId, setSelectedPotId] = useState(null);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState(null);

  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [isSaveGoalOpen, setIsSaveGoalOpen] = useState(false);

  useEffect(() => {
    const fetchActionPlan = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        
        const response = await fetch(`${ACTION_PLAN_BASE_URL}/action-plan/${userId}`);
        
        if (!response.ok) {
          throw new Error("Failed to fetch action plan");
        }
        
        const data = await response.json();
        setActionPlan(data);
        
        if (data.pots && data.pots.length > 0) {
          const firstPot = data.pots[0];
          setSelectedPotId(firstPot.id);
          
          const firstIncomplete = firstPot.objectives.find(obj => obj.status === "active");
          if (firstIncomplete) {
            setSelectedObjectiveId(firstIncomplete.id);
          }
        }
      } catch (error) {
        console.error("Error fetching action plan:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActionPlan();
  }, [userId]);

  const handleCheckIn = () => {
    const pot = actionPlan.pots.find((p) => p.id === selectedPotId);
    const objective = pot?.objectives.find(obj => obj.id === selectedObjectiveId);

    if (pot && objective) {
      setIsCheckInOpen(true);
    }
  };

  const handleCheckInSave = async (checkInData) => {
    const pot = actionPlan.pots.find((p) => p.id === selectedPotId);
    
    try {
      await actionPlanService.submitCheckIn(
        userId,
        pot.id,
        checkInData.items,
        checkInData.message
      );
      
      console.log("✅ Check-in saved");
      
      const response = await fetch(`${ACTION_PLAN_BASE_URL}/action-plan/${userId}`);
      const data = await response.json();
      setActionPlan(data);
      
      setIsCheckInOpen(false);
    } catch (error) {
      console.error("Error saving check-in:", error);
      alert("Failed to save check-in. Please try again.");
    }
  };

  const handleCompleteGoal = async () => {
    const pot = actionPlan.pots.find((p) => p.id === selectedPotId);
    const objective = pot?.objectives.find(obj => obj.id === selectedObjectiveId);

    if (!objective) return;

    try {
      await actionPlanService.completeObjective(userId, objective.id);
      
      console.log("✅ Objective completed");
      
      const response = await fetch(`${ACTION_PLAN_BASE_URL}/action-plan/${userId}`);
      const data = await response.json();
      setActionPlan(data);
      
      const updatedPot = data.pots.find((p) => p.id === selectedPotId);
      const nextIncomplete = updatedPot?.objectives.find(obj => obj.status === "active");
      if (nextIncomplete) {
        setSelectedObjectiveId(nextIncomplete.id);
      }
      
      setIsSaveGoalOpen(true);
    } catch (error) {
      console.error("Error completing objective:", error);
      alert("Failed to complete objective. Please try again.");
    }
  };

  const handleNextObjective = () => {
    setIsSaveGoalOpen(false);
  };

  const handlePotClick = (potId) => {
    setSelectedPotId(potId);
    
    const newPot = actionPlan.pots.find(p => p.id === potId);
    const firstIncomplete = newPot?.objectives.find(obj => obj.status === "active");
    if (firstIncomplete) {
      setSelectedObjectiveId(firstIncomplete.id);
    }
  };

  const handleObjectiveClick = (objectiveId) => {
    setSelectedObjectiveId(objectiveId);
  };

  // ✅ ADD THIS: Direct navigation handler
  const handleGoToActionPlan = () => {
    navigate('/learner/action-plan');
  };

  if (loading) {
    return (
      <div className="w-full bg-transparent flex items-center justify-center py-20">
        <p className="text-[18px] text-gray-600">Loading your action plan...</p>
      </div>
    );
  }

  if (!actionPlan || !actionPlan.pots || actionPlan.pots.length === 0) {
    return (
      <div className="w-full bg-transparent flex items-center justify-center py-20">
        <p className="text-[18px] text-gray-600">No action plan found. Create one to get started!</p>
      </div>
    );
  }

  const currentPot = actionPlan.pots.find((pot) => pot.id === selectedPotId);
  const currentObjective = currentPot?.objectives.find(obj => obj.id === selectedObjectiveId);

  return (
    <div className="w-full bg-transparent">
      <div className="flex items-center justify-between px-8 mb-5">
        <div className="flex items-center gap-4">
          <img
            src={actionPot1}
            alt="Your Action Plan Pot"
            className="w-[58px] h-[107px] object-contain"
          />
          <div className="pl-6">
            <h2 className="text-[34px] leading-[1.35] font-medium text-black">
              Your Action Plan
            </h2>
            <p className="mt-1 text-[18px] leading-[1.45] font-normal text-black/80">
              Build your promotion action plan, as you like it.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoToActionPlan} // ✅ USE DIRECT NAVIGATION
          className="h-[46px] min-w-[220px] px-5 rounded-[6.2px] bg-black text-white text-[17px] font-medium flex items-center justify-center hover:bg-gray-800 transition-colors"
        >
          Design Your Garden
        </button>
      </div>

      <div className="w-full">
        <div className="mx-auto w-[1189px] min-h-[857px] rounded-[20px] border border-[#E3E3E3] bg-white">
          <div className="flex ml-6 pt-12 px-4 pb-4">
            <LeftPotPanel
              pot={currentPot}
              onCheckIn={handleCheckIn}
              onCompleteGoal={handleCompleteGoal}
            />
            <RightInitiativesPanel 
              pot={currentPot}
              selectedObjectiveId={selectedObjectiveId}
              onObjectiveClick={handleObjectiveClick}
            />
          </div>

          <GoalPotsSection
            allPots={actionPlan.pots}
            selectedPotId={selectedPotId}
            onPotClick={handlePotClick}
          />
        </div>
      </div>

      <CheckIn
        isOpen={isCheckInOpen}
        onClose={() => setIsCheckInOpen(false)}
        onSave={handleCheckInSave}
        pot={currentPot}
        objective={currentObjective}
      />

      <SaveGoals
        isOpen={isSaveGoalOpen}
        onClose={() => setIsSaveGoalOpen(false)}
        onNextObjective={handleNextObjective}
        objectiveText={currentObjective?.title || ""}
      />
    </div>
  );
};

export default MyActionsIntro;